import {
  CalendarBlankIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import FilterSelect from '@/components/ui/filter-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import SearchableSelect from '@/components/ui/searchable-select';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { cn } from '@/lib/utils';
import type {
  OrderCustomerData as Customer,
  DatePickerProps,
  OrderFormData,
  OrderFormProps,
  OrderItemData as OrderItem,
  OrderUpdateFormData,
  OrderProductData as Product,
  StepIndicatorProps,
} from '@/types/module';
import {
  PAYMENT_STATUS_OPTIONS as paymentStatusOptions,
  ORDER_STATUS_OPTIONS as statusOptions,
} from '@/types/module';
import { capitalizeFirstLetter, formatCurrency } from '@/utils/formatting';
// Step indicator component
const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  totalSteps,
  stepLabels,
}) => {
  return (
    <div className='flex items-center justify-between mb-8'>
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        const isLast = stepNumber === totalSteps;

        return (
          <React.Fragment key={stepNumber}>
            <div className='flex flex-col items-center'>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isCompleted
                    ? 'bg-success-60 text-white'
                    : isActive
                      ? 'bg-success-60 text-white'
                      : 'bg-gray-20 text-gray-60'
                }`}
              >
                {isCompleted ? <CheckIcon className='w-4 h-4' /> : stepNumber}
              </div>
              <span className='text-xs mt-2 text-gray-60 text-center'>
                {stepLabels[index]}
              </span>
            </div>
            {!isLast && (
              <div
                className={`flex-1 h-0.5 mx-4 ${
                  isCompleted || (isActive && stepNumber > 1)
                    ? 'bg-success-60'
                    : 'bg-gray-20'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Date Picker Component
const DatePicker: React.FC<DatePickerProps> = ({
  onDateChange,
  placeholder = 'Pick a date',
  error,
  label,
  selectedDate,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className='space-y-2'>
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            hierarchy='outlined'
            color='gray'
            className={cn(
              'w-full justify-start text-left font-normal',
              !selectedDate && 'text-muted-foreground',
              error && 'border-destructive-50'
            )}
          >
            <CalendarBlankIcon className='mr-2 h-4 w-4' />
            {selectedDate ? (
              format(selectedDate, 'PPP')
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='single'
            selected={selectedDate}
            onSelect={selectedDate => {
              onDateChange(selectedDate);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {error && <p className='text-sm text-destructive-60'>{error}</p>}
    </div>
  );
};

// Constants are now imported from @/types/module

const OrderForm: React.FC<OrderFormProps> = ({
  mode,
  order,
  onSuccess,
  onCancel,
}) => {
  const api = useEnhancedApi();
  const { showSuccess, showError } = useToast();
  const get = api.get;
  const createOrder = api.orders.create;
  const reviewOrder = api.orders.review;
  const updateStatus = api.orders.updateStatus;

  // Create mode state
  const [currentStep, setCurrentStep] = useState(1);
  const [productCache, setProductCache] = useState<Record<string, Product>>({});
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [orderItems, setOrderItems] = useState<OrderItem[]>([
    {
      id: Date.now(),
      name: '',
      price: 0,
      _id: '',
      quantity: 1,
      variant: '',
      variantId: '',
    },
  ]);
  const [deliveryInfo, setDeliveryInfo] = useState({
    deliveryMethod: '',
    deliveryFee: 0,
    deliveryNote: '',
  });

  // Common state
  const [isLoading, setIsLoading] = useState(false);

  const stepLabels = [
    'Customer and Order Details',
    'Add Products',
    'Delivery Information',
    'Summary',
  ];
  const totalSteps = 4;

  // Form configuration based on mode
  const createFormConfig = useForm<OrderFormData>({
    defaultValues: {
      orderDate: new Date().toISOString().split('T')[0],
      customerId: '',
      paymentStatus: 'unpaid',
      deliveryMethod: 'pick-up',
      deliveryFee: '',
      deliveryNote: '',
    },
  });

  const updateFormConfig = useForm<OrderUpdateFormData>({
    defaultValues: {
      status: order?.status as any,
      paymentStatus: order?.paymentStatus as any,
      notes: order?.notes || '',
    },
  });

  const formConfig = mode === 'create' ? createFormConfig : updateFormConfig;
  const { control, handleSubmit, watch, trigger, reset } = formConfig;

  // Watch form changes for create mode
  useEffect(() => {
    if (mode === 'create') {
      const subscription = watch(value => {
        const createValue = value as OrderFormData;
        setDeliveryInfo({
          deliveryMethod: createValue.deliveryMethod || '',
          deliveryFee: Number(createValue.deliveryFee) || 0,
          deliveryNote: createValue.deliveryNote || '',
        });

        const cid = (createValue.customerId || '').trim();
        if (cid && (!selectedCustomer || selectedCustomer._id !== cid)) {
          handleCustomerSelection(cid);
        }
      });
      return () => {
        if (
          subscription &&
          typeof subscription === 'object' &&
          'unsubscribe' in subscription
        ) {
          subscription.unsubscribe();
        }
      };
    }
  }, [watch, mode, selectedCustomer]);

  // Handle customer selection for create mode
  const handleCustomerSelection = async (customerId: string) => {
    if (!customerId) {
      setSelectedCustomer(null);
      return;
    }
    try {
      const resp = await get(`/customers/${customerId}`);
      if ((resp as any)?.success) {
        const customer =
          (resp as any)?.data?.customer || (resp as any)?.data || null;
        setSelectedCustomer(customer);
      } else {
        setSelectedCustomer(null);
      }
    } catch (error) {
      setSelectedCustomer(null);
    }
  };

  // Product management functions for create mode
  const addProduct = () => {
    const newItems = [
      ...orderItems,
      {
        id: Date.now(),
        name: '',
        price: 0,
        _id: '',
        quantity: 1,
        variant: '',
        variantId: '',
      },
    ];
    setOrderItems(newItems);
  };

  const removeProduct = (itemId: number) => {
    if (orderItems.length > 1) {
      const newItems = orderItems.filter(item => item.id !== itemId);
      setOrderItems(newItems);
    }
  };

  const updateProductField = (
    itemId: number,
    field: keyof OrderItem,
    value: string | number
  ) => {
    const newItems = orderItems.map(item =>
      item.id === itemId
        ? {
            ...item,
            [field]:
              field === 'quantity' || field === 'price' ? Number(value) : value,
          }
        : item
    );
    setOrderItems(newItems);
  };

  const handleProductSelection = async (itemId: number, productId: string) => {
    if (
      !productId ||
      productId === '' ||
      productId === null ||
      productId === undefined
    ) {
      const newItems = orderItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              _id: '',
              name: '',
              price: 0,
              quantity: item.quantity || 1,
              variant: '',
              variantId: '',
            }
          : item
      );
      setOrderItems(newItems);
      return;
    }

    // Optimistically reflect selection in UI immediately
    setOrderItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              _id: productId,
              name: item.name || '',
              price: item.price || 0,
              variant: '',
              variantId: '',
            }
          : item
      )
    );

    try {
      const resp = await get(`/inventories/${productId}`);
      if ((resp as any)?.success) {
        const selectedProduct: Product =
          (resp as any)?.data?.inventory ||
          (resp as any)?.data?.product ||
          (resp as any)?.data?.item ||
          (resp as any)?.data;

        if (selectedProduct && selectedProduct._id) {
          setProductCache(prev => ({ ...prev, [productId]: selectedProduct }));

          setOrderItems(prevItems =>
            prevItems.map(item =>
              item.id === itemId
                ? {
                    ...item,
                    _id: productId,
                    name: selectedProduct.name,
                    price: !selectedProduct.isVariant
                      ? selectedProduct.price
                      : 0,
                    quantity: !selectedProduct.isVariant
                      ? item.quantity || 1
                      : 1,
                    variant: '',
                    variantId: '',
                  }
                : item
            )
          );
        }
      }
    } catch (error) {
      showError('Failed to fetch product details. Please try again.');
    }
  };

  const handleVariantSelection = (itemId: number, variantId: string) => {
    const productItem = orderItems.find(p => p.id === itemId);
    if (productItem) {
      const selectedProduct = productCache[productItem._id];

      if (!variantId) {
        const newItems = orderItems.map(item =>
          item.id === itemId
            ? {
                ...item,
                variant: '',
                variantId: '',
                price: selectedProduct?.price || item.price,
              }
            : item
        );
        setOrderItems(newItems);
        return;
      }

      const selectedVariant = selectedProduct?.variant?.find(
        (v: any) => v._id === variantId
      );

      if (selectedVariant) {
        const newItems = orderItems.map(item =>
          item.id === itemId
            ? {
                ...item,
                variant: selectedVariant.name,
                variantId: variantId,
                price: selectedVariant.price || item.price,
              }
            : item
        );
        setOrderItems(newItems);
      }
    }
  };

  const adjustQuantity = (itemId: number, adjustment: number) => {
    const newItems = orderItems.map(item =>
      item.id === itemId
        ? {
            ...item,
            quantity: Math.max(1, item.quantity + adjustment),
          }
        : item
    );
    setOrderItems(newItems);
  };

  const getVariantOptions = (productId: string) => {
    const selectedProduct = productCache[productId];

    if (
      selectedProduct?.isVariant &&
      selectedProduct?.variant &&
      selectedProduct.variant.length > 0
    ) {
      return selectedProduct.variant.map(
        (variant: { _id: string; name: string }) => ({
          value: variant._id,
          label: variant.name,
        })
      );
    }

    return [];
  };

  const hasVariants = (productId: string) => {
    const selectedProduct = productCache[productId];
    return (
      selectedProduct?.isVariant &&
      selectedProduct?.variant &&
      selectedProduct.variant.length > 0
    );
  };

  // Step validation for create mode
  const validateStep = async (step: number): Promise<boolean> => {
    if (step === 1) {
      const fieldsToValidate: (keyof OrderFormData)[] = [
        'orderDate',
        'customerId',
        'paymentStatus',
      ];
      return await trigger(fieldsToValidate as any);
    } else if (step === 2) {
      const validProducts = orderItems.filter(
        item => item._id && item._id.trim() !== '' && item.quantity > 0
      );

      if (validProducts.length === 0) {
        showError('Please select at least one product with quantity');
        return false;
      }
      return true;
    } else if (step === 3) {
      const fieldsToValidate: (keyof OrderFormData)[] = ['deliveryMethod'];
      return await trigger(fieldsToValidate as any);
    }
    return true;
  };

  // Navigation functions for create mode
  const handleNextStep = async () => {
    if (mode === 'update') return;

    const isValid = await validateStep(currentStep);

    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else if (isValid && currentStep === totalSteps) {
      await handleSubmit(onSubmit as any)();
    }
  };

  const handlePrevStep = () => {
    if (mode === 'update') return;

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Form submission for create mode
  const onSubmitCreate = async (formData: OrderFormData) => {
    if (!selectedCustomer) {
      showError('Please select a customer');
      return;
    }

    const validProducts = orderItems.filter(
      item => item._id && item.quantity > 0
    );
    if (validProducts.length === 0) {
      showError('Please add at least one product to the order');
      return;
    }

    setIsLoading(true);
    try {
      const groupedItems = new Map();

      validProducts.forEach(item => {
        const baseProductId = item._id;

        if (!groupedItems.has(baseProductId)) {
          groupedItems.set(baseProductId, {
            _id: baseProductId,
            variants: [],
            nonVariantQuantity: 0,
            nonVariantPrice: 0,
            hasVariants: false,
          });
        }

        const grouped = groupedItems.get(baseProductId);

        if (item.variantId) {
          grouped.hasVariants = true;
          grouped.variants.push({
            variantId: item.variantId,
            quantity: item.quantity,
            unitPrice: item.price,
          });
        } else {
          grouped.nonVariantQuantity += item.quantity;
          grouped.nonVariantPrice = item.price;
        }
      });

      const Items = Array.from(groupedItems.values()).map(grouped => {
        if (grouped.hasVariants && grouped.variants.length > 0) {
          return {
            itemId: grouped._id,
            variants: grouped.variants.map(
              (variant: {
                variantId: string;
                quantity: number;
                unitPrice: number;
              }) => ({
                variantId: variant.variantId,
                quantity: variant.quantity,
                unitPrice: variant.unitPrice,
              })
            ),
          };
        } else {
          return {
            itemId: grouped._id,
            quantity: grouped.nonVariantQuantity,
            unitPrice: grouped.nonVariantPrice,
          };
        }
      });

      const normalizedDeliveryMethod =
        formData.deliveryMethod === 'pick-up'
          ? 'pickup'
          : formData.deliveryMethod;

      const orderData = {
        customerId: (formData as any)?.customerId || selectedCustomer._id,
        items: Items,
        delivery: {
          deliveryFee: Number(formData.deliveryFee) || 0,
          deliveryMethod: normalizedDeliveryMethod,
          deliveryNote: formData.deliveryNote || '',
        },
        source: 'manual',
      };

      const response = await createOrder(orderData);

      if (response.success) {
        showSuccess(response.message || 'Order created successfully');
        resetForm();
        await onSuccess();
      } else {
        showError(response?.message || 'Failed to create order');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      showError(`Failed to create order: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Form submission for update mode
  const onSubmitUpdate = async (formData: OrderUpdateFormData) => {
    if (!order) return;

    if (!formData.status) {
      showError('Order status is required');
      return;
    }

    setIsLoading(true);
    try {
      const updatePromises: Promise<any>[] = [];
      const hasStatusChange = formData.status !== order.status;
      const hasNotes = formData.notes && formData.notes.trim().length > 0;

      if (hasStatusChange) {
        if (hasNotes) {
          updatePromises.push(
            reviewOrder(order._id, {
              status: formData.status,
              notes: formData.notes,
            })
          );
        } else {
          updatePromises.push(updateStatus(order._id, formData.status));
        }
      } else if (hasNotes) {
        updatePromises.push(
          reviewOrder(order._id, {
            status: order.status,
            notes: formData.notes,
          })
        );
      }

      if (updatePromises.length === 0) {
        showError('No changes detected');
        setIsLoading(false);
        return;
      }

      const responses = await Promise.all(updatePromises);
      const allSuccessful = responses.every(response => response.success);

      if (allSuccessful) {
        showSuccess('Order updated successfully');
        await onSuccess();
      } else {
        const failedResponses = responses.filter(response => !response.success);
        const errorMessage =
          failedResponses[0]?.message || 'Failed to update order';
        showError(errorMessage);
      }
    } catch (error) {
      showError('An error occurred while updating the order');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = mode === 'create' ? onSubmitCreate : onSubmitUpdate;

  const resetForm = () => {
    reset();
    if (mode === 'create') {
      setOrderItems([
        {
          id: Date.now(),
          name: '',
          price: 0,
          _id: '',
          quantity: 1,
          variant: '',
          variantId: '',
        },
      ]);
      setSelectedCustomer(null);
      setCurrentStep(1);
      setDeliveryInfo({
        deliveryMethod: '',
        deliveryFee: 0,
        deliveryNote: '',
      });
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  // Expose handlers to global window object
  useEffect(() => {
    if (mode === 'create') {
      const handlers = {
        handleNextStep,
        handlePrevStep,
        handleCancel,
        currentStep,
        totalSteps,
        isLoading,
      };

      (window as any).orderCreateHandlers = handlers;

      return () => {
        delete (window as any).orderCreateHandlers;
      };
    } else {
      const handlers = {
        handleSubmit: handleSubmit(onSubmit as any),
        handleCancel,
        isLoading,
      };

      (window as any).orderUpdateHandlers = handlers;

      return () => {
        delete (window as any).orderUpdateHandlers;
      };
    }
  }, [
    mode,
    handleNextStep,
    handlePrevStep,
    handleCancel,
    handleSubmit,
    currentStep,
    totalSteps,
    isLoading,
    onSubmit,
  ]);

  // Calculate totals for create mode
  const subtotal = orderItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const total = subtotal + deliveryInfo.deliveryFee;

  // Render create mode steps
  const renderCreateStepContent = () => {
    const createControl = control as any;

    switch (currentStep) {
      case 1:
        return (
          <div className='space-y-6'>
            <Controller
              name='orderDate'
              control={createControl}
              rules={{ required: 'Order date is required' }}
              render={({ field, fieldState }) => (
                <DatePicker
                  selectedDate={field.value ? new Date(field.value) : undefined}
                  onDateChange={date => {
                    if (date) {
                      field.onChange(format(date, 'yyyy-MM-dd'));
                    } else {
                      field.onChange('');
                    }
                  }}
                  label='Order Date'
                  placeholder='Select order date'
                  error={fieldState?.error?.message}
                />
              )}
            />

            <Controller
              name='customerId'
              control={createControl}
              rules={{ required: 'Customer is required' }}
              render={({ field, fieldState }) => (
                <SearchableSelect
                  name='customerId'
                  searchId='order-form-customer-select'
                  options={[]}
                  className='w-full'
                  label='Customer'
                  placeholder='Search or select customer'
                  error={fieldState?.error?.message}
                  valueLabel={
                    selectedCustomer
                      ? selectedCustomer.contactName ||
                        selectedCustomer.businessName ||
                        'Unknown Customer'
                      : undefined
                  }
                  onChange={value => {
                    field.onChange(value);
                    handleCustomerSelection(value);
                  }}
                  value={field.value}
                  onSearch={async q => {
                    const params = q
                      ? { search: q, page: '1', limit: '20' }
                      : { page: '1', limit: '50' };
                    const resp = await get('/customers', params);
                    if (resp.success) {
                      const arr =
                        (resp as any)?.data?.customers?.docs ||
                        (resp as any)?.data?.customers ||
                        (resp as any)?.data?.docs ||
                        [];
                      return arr.map((c: any) => ({
                        value: c._id,
                        label:
                          c.contactName || c.businessName || 'Unknown Customer',
                      }));
                    }
                    return [];
                  }}
                />
              )}
            />

            {selectedCustomer && (
              <>
                <Input
                  value={
                    selectedCustomer.accountType
                      ? selectedCustomer.accountType.toUpperCase()
                      : ''
                  }
                  className='w-full'
                  label='Account Type'
                  placeholder='Account Type'
                  readOnly
                  disabled
                />

                <Input
                  value={
                    selectedCustomer.creditStatus?.type
                      ? selectedCustomer.creditStatus.type
                          .charAt(0)
                          .toUpperCase() +
                        selectedCustomer.creditStatus.type.slice(1)
                      : ''
                  }
                  className='w-full'
                  label='Payment Term'
                  placeholder='Payment Term'
                  readOnly
                  disabled
                />

                <Input
                  value={
                    selectedCustomer.creditStatus?.period
                      ? `${selectedCustomer.creditStatus.period} Days`
                      : ''
                  }
                  className='w-full'
                  label='Payment Period'
                  placeholder='Payment Period'
                  readOnly
                  disabled
                />

                <Input
                  value={
                    selectedCustomer.creditStatus?.limit
                      ? `₦${selectedCustomer.creditStatus.limit.toLocaleString()}`
                      : ''
                  }
                  className='w-full'
                  label='Available Credit'
                  placeholder='Available Credit'
                  readOnly
                  disabled
                />

                <Input
                  value={selectedCustomer.creditStatus?.paymentDueDate || ''}
                  className='w-full'
                  label='Payment Due Day'
                  placeholder='Payment Due Day'
                  readOnly
                  disabled
                />

                <Controller
                  name='paymentStatus'
                  control={createControl}
                  rules={{ required: 'Payment status is required' }}
                  render={({ field, fieldState }) => (
                    <FilterSelect
                      name='paymentStatus'
                      value={field.value}
                      options={[
                        { value: 'unpaid', label: 'Unpaid' },
                        { value: 'paid', label: 'Paid' },
                      ]}
                      className='w-full'
                      label='Payment Status'
                      placeholder='Select payment status'
                      error={fieldState?.error?.message}
                      onChange={field.onChange}
                    />
                  )}
                />
              </>
            )}
          </div>
        );

      case 2:
        return (
          <div className='space-y-6'>
            {orderItems.map(item => (
              <div key={item.id} className='space-y-4'>
                <div className='flex flex-col gap-4 p-4 border border-gray-30 rounded-lg'>
                  <div className='flex items-center gap-4'>
                    <div className='w-full'>
                      <SearchableSelect
                        name={`product_id_${item.id}`}
                        searchId={`order-form-product-select-${item.id}`}
                        options={[]}
                        className='w-full'
                        label='Item'
                        placeholder='Search or select item'
                        onChange={selectedValue => {
                          handleProductSelection(item.id, selectedValue);
                        }}
                        value={item._id}
                        valueLabel={productCache[item._id]?.name}
                        onSearch={async q => {
                          const params = q
                            ? { search: q, page: '1', limit: '20' }
                            : { page: '1', limit: '50' };
                          const resp = await get('/inventories', params);
                          if (resp.success) {
                            const raw: any =
                              (resp as any)?.data?.inventory?.docs ??
                              (resp as any)?.data?.inventory ??
                              (resp as any)?.data?.docs ??
                              (resp as any)?.data ??
                              [];

                            const list: any[] = Array.isArray(raw)
                              ? raw
                              : raw && typeof raw === 'object'
                                ? Object.values(raw).filter(
                                    (v: any) =>
                                      v && typeof v === 'object' && v._id
                                  )
                                : [];

                            return list.map((product: any) => ({
                              value: product._id,
                              label: product.name,
                            }));
                          }
                          return [];
                        }}
                      />
                    </div>
                    {orderItems.length > 1 && (
                      <button
                        type='button'
                        onClick={() => removeProduct(item.id)}
                        className='text-destructive-50 hover:text-destructive-70 p-2'
                      >
                        <TrashIcon size={20} />
                      </button>
                    )}
                  </div>

                  <div className='flex items-end gap-4 mb-4'>
                    {Boolean(item._id) && hasVariants(item._id) && (
                      <div className='w-2/4'>
                        <FilterSelect
                          name={`variant_${item.id}`}
                          options={getVariantOptions(item._id)}
                          className='w-full'
                          label='Variant'
                          placeholder={
                            !item._id
                              ? 'Select item first'
                              : !hasVariants(item._id)
                                ? 'No variants available'
                                : 'Select variant'
                          }
                          onChange={selectedValue => {
                            handleVariantSelection(item.id, selectedValue);
                          }}
                          value={item.variantId || ''}
                          disabled={!item._id || !hasVariants(item._id)}
                        />
                      </div>
                    )}

                    {Boolean(item._id) && (
                      <>
                        <div className='w-1/5'>
                          <label className='block text-sm font-medium text-gray-70 mb-1'>
                            Quantity
                          </label>
                          <div className='flex items-center border border-gray-30 rounded-full overflow-hidden'>
                            <button
                              type='button'
                              onClick={() => adjustQuantity(item.id, -1)}
                              className='px-3 py-2 hover:bg-gray-10 disabled:opacity-50'
                              disabled={item.quantity <= 1}
                            >
                              -
                            </button>
                            <input
                              type='number'
                              value={item.quantity}
                              onChange={e =>
                                updateProductField(
                                  item.id,
                                  'quantity',
                                  e.target.value
                                )
                              }
                              className='flex-1 px-3 py-2 text-center outline-none border-none min-w-0'
                              min='1'
                            />
                            <button
                              type='button'
                              onClick={() => adjustQuantity(item.id, 1)}
                              className='px-3 py-2 hover:bg-gray-10'
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className='w-1/5'>
                          <Input
                            value={item.price}
                            className='w-full'
                            label='Unit Price'
                            placeholder='Unit Price'
                            type='number'
                            onChange={e =>
                              updateProductField(
                                item.id,
                                'price',
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className='w-1/5'>
                          <Input
                            value={(
                              item.price * item.quantity
                            ).toLocaleString()}
                            className='w-full'
                            label='Sub Total'
                            placeholder='Amount'
                            readOnly
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div
              className='flex justify-center bg-[#E8F2E8] rounded-full p-3 cursor-pointer hover:bg-[#d4e8d4] transition-colors'
              onClick={addProduct}
            >
              <button
                type='button'
                className='flex items-center justify-center gap-2 text-[#047401] font-medium'
              >
                <PlusIcon size={16} />
                Add New Item
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className='space-y-6'>
            <Controller
              name='deliveryMethod'
              control={createControl}
              rules={{ required: 'Delivery method is required' }}
              render={({ field, fieldState }) => (
                <FilterSelect
                  name='deliveryMethod'
                  value={field.value}
                  className='w-full'
                  options={[
                    { value: 'pick-up', label: 'Pick up' },
                    { value: 'dispatch', label: 'Dispatch' },
                  ]}
                  label='Delivery method'
                  placeholder='select delivery method'
                  error={fieldState?.error?.message}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              name='deliveryFee'
              control={createControl}
              rules={{
                required: 'Delivery fee is required',
                min: { value: 0, message: 'Delivery fee cannot be negative' },
              }}
              render={({ field, fieldState }) => (
                <Input
                  value={field.value}
                  className='w-full'
                  placeholder='Enter delivery fee'
                  error={fieldState?.error?.message}
                  onChange={field.onChange}
                  autoComplete='off'
                  label='Delivery Fee'
                  type='number'
                  min='0'
                />
              )}
            />

            <Controller
              name='deliveryNote'
              control={createControl}
              render={({ field, fieldState }) => (
                <div className='space-y-2'>
                  <label className='block text-sm font-medium text-gray-70'>
                    Delivery Notes
                  </label>
                  <div className='relative'>
                    <textarea
                      value={field.value || ''}
                      onChange={e => {
                        const value = e.target.value;
                        if (value.length <= 300) {
                          field.onChange(value);
                        }
                      }}
                      className='w-full p-3 border border-gray-30 rounded-lg resize-none focus:ring-2 focus:ring-success-50 focus:border-transparent outline-none min-h-[120px]'
                      placeholder='Enter your main text here...'
                      maxLength={300}
                    />

                    <div className='flex justify-end mt-1'>
                      <span className='text-xs text-gray-50'>
                        {(field.value || '').length}/300
                      </span>
                    </div>
                  </div>

                  {fieldState?.error?.message && (
                    <p className='text-sm text-destructive-60'>
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        );

      case 4:
        return (
          <div className='bg-white rounded-lg space-y-6'>
            {/* Customer Details */}
            <div className='border border-[#C9C9C9] rounded-lg overflow-hidden'>
              <div className='bg-[#F5F5F5] px-6 py-3'>
                <h3 className='text-sm font-semibold text-[#2B2B2B]'>
                  Customer Details
                </h3>
              </div>
              <div className='divide-y divide-gray-200'>
                <div className='flex items-center justify-between px-6 py-4'>
                  <span className='text-sm text-[#4E4E4E]'>Customer Name</span>
                  <span className='text-sm font-medium text-[#2B2B2B]'>
                    {selectedCustomer?.contactName ||
                      selectedCustomer?.businessName ||
                      'N/A'}
                  </span>
                </div>
                <div className='flex items-center justify-between px-6 py-4'>
                  <span className='text-sm text-[#4E4E4E]'>Customer Type</span>
                  <span className='text-sm font-medium text-[#2B2B2B] uppercase'>
                    {selectedCustomer?.accountType || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className='border border-[#C9C9C9] rounded-lg overflow-hidden'>
              <div className='overflow-x-auto'>
                <table className='min-w-full'>
                  <thead className='bg-[#F5F5F5]'>
                    <tr>
                      <th className='px-6 py-3 text-left text-sm font-semibold text-[#2B2B2B]'>
                        Item Name
                      </th>
                      <th className='px-6 py-3 text-right text-sm font-semibold text-[#2B2B2B]'>
                        Quantity
                      </th>
                      <th className='px-6 py-3 text-right text-sm font-semibold text-[#2B2B2B]'>
                        Unit
                      </th>
                      <th className='px-6 py-3 text-right text-sm font-semibold text-[#2B2B2B]'>
                        Sub Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200'>
                    {orderItems
                      .filter(item => item._id)
                      .map((item, index) => (
                        <tr key={item.id || index}>
                          <td className='px-6 py-4 text-sm text-[#2B2B2B]'>
                            {item.name}
                            {item.variant && (
                              <span className='text-gray-50 ml-1'>
                                - {item.variant}
                              </span>
                            )}
                          </td>
                          <td className='px-6 py-4 text-sm text-right text-[#2B2B2B]'>
                            {item.quantity}
                          </td>
                          <td className='px-6 py-4 text-sm text-right text-[#2B2B2B]'>
                            ₦{item.price.toLocaleString()}
                          </td>
                          <td className='px-6 py-4 text-sm text-right text-[#2B2B2B]'>
                            ₦{(item.price * item.quantity).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Delivery Information */}
            <div className='border border-[#C9C9C9] rounded-lg overflow-hidden'>
              <div className='bg-[#F5F5F5] px-6 py-3'>
                <h3 className='text-sm font-semibold text-[#2B2B2B]'>
                  Delivery Information
                </h3>
              </div>
              <div className='divide-y divide-gray-200'>
                <div className='flex items-center justify-between px-6 py-4'>
                  <span className='text-sm text-[#4E4E4E]'>
                    Delivery Method
                  </span>
                  <span className='text-sm font-medium text-[#2B2B2B] capitalize'>
                    {deliveryInfo.deliveryMethod === 'pick-up'
                      ? 'Pick up'
                      : 'Dispatch'}
                  </span>
                </div>
                <div className='flex items-center justify-between px-6 py-4'>
                  <span className='text-sm text-[#4E4E4E]'>Delivery Fee</span>
                  <span className='text-sm font-medium text-[#2B2B2B]'>
                    ₦{deliveryInfo.deliveryFee.toLocaleString()}
                  </span>
                </div>
                <div className='flex items-center justify-between px-6 py-4'>
                  <span className='text-sm text-[#4E4E4E]'>Delivery Notes</span>
                  <span className='text-sm font-medium text-[#2B2B2B] text-right max-w-xs truncate'>
                    {deliveryInfo.deliveryNote || 'No notes provided'}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Summary Totals */}
            <div className='border border-[#C9C9C9] rounded-lg overflow-hidden'>
              <div className='divide-y divide-gray-200'>
                <div className='flex items-center justify-between px-6 py-4'>
                  <span className='text-sm font-medium text-[#2B2B2B]'>
                    Sub Total
                  </span>
                  <span className='text-sm font-medium text-[#2B2B2B]'>
                    ₦{subtotal.toLocaleString()}
                  </span>
                </div>
                <div className='flex items-center justify-between px-6 py-4'>
                  <span className='text-sm font-medium text-[#2B2B2B]'>
                    Delivery Fee
                  </span>
                  <span className='text-sm font-medium text-[#2B2B2B]'>
                    ₦{deliveryInfo.deliveryFee.toLocaleString()}
                  </span>
                </div>
                <div className='bg-[#F5F5F5] px-6 py-4'>
                  <div className='flex items-center justify-between'>
                    <span className='text-lg font-bold text-[#2B2B2B]'>
                      Total
                    </span>
                    <span className='text-lg font-bold text-[#2B2B2B]'>
                      ₦{total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render update mode form
  const renderUpdateForm = () => {
    if (!order) return null;

    const updateWatch = watch as any;
    const updateControl = control as any;
    const watchedStatus = updateWatch('status');
    const watchedPaymentStatus = updateWatch('paymentStatus');

    return (
      <div className='space-y-6'>
        {/* Order Information Header */}
        <div className='bg-gray-5 rounded-lg p-6 space-y-4'>
          <div className='border-b border-gray-20 pb-4'>
            <h3 className='text-lg font-semibold text-gray-90'>Order Update</h3>
            <p className='text-sm text-gray-60 mt-1'>
              Updating order: {order.orderId || order.orderNumber || 'N/A'}
            </p>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {/* Customer Information */}
            <div className='space-y-4'>
              <h4 className='text-sm font-medium text-gray-90 border-b border-gray-20 pb-2'>
                Customer Information
              </h4>
              <div className='space-y-3 text-sm'>
                <div className='flex justify-between items-center'>
                  <span className='text-gray-60'>Customer Name:</span>
                  <span className='font-medium text-gray-90'>
                    {order.customer?.contactName ||
                      order.customer?.businessName ||
                      'N/A'}
                  </span>
                </div>
                <div className='flex justify-between items-center'>
                  <span className='text-gray-60'>Created By:</span>
                  <span className='font-medium text-gray-90'>
                    {order.createdBy?.username || 'N/A'}
                  </span>
                </div>
                <div className='flex justify-between items-center'>
                  <span className='text-gray-60'>Created Date:</span>
                  <span className='font-medium text-gray-90'>
                    {order.createdAt
                      ? new Date(order.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Status Information */}
            <div className='space-y-4'>
              <h4 className='text-sm font-medium text-gray-90 border-b border-gray-20 pb-2'>
                Current Status
              </h4>
              <div className='space-y-3 text-sm'>
                <div className='flex justify-between items-center'>
                  <span className='text-gray-60'>Order Status:</span>
                  <span className='font-medium px-2 py-1 bg-brand-10 text-brand-80 rounded-full text-xs uppercase'>
                    {capitalizeFirstLetter(order.status)}
                  </span>
                </div>
                <div className='flex justify-between items-center'>
                  <span className='text-gray-60'>Payment Status:</span>
                  <span
                    className={`font-medium px-2 py-1 rounded-full text-xs uppercase ${
                      order.paymentStatus === 'paid'
                        ? 'bg-success-10 text-success-80'
                        : order.paymentStatus === 'unpaid'
                          ? 'bg-destructive-10 text-destructive-80'
                          : 'bg-warning-10 text-warning-80'
                    }`}
                  >
                    {capitalizeFirstLetter(order.paymentStatus || 'Pending')}
                  </span>
                </div>
                <div className='flex justify-between items-center'>
                  <span className='text-gray-60'>Total Amount:</span>
                  <span className='font-medium text-gray-90'>
                    {order.totalAmount
                      ? formatCurrency(order.totalAmount)
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Items Summary */}
          {order.Items && order.Items.length > 0 && (
            <div className='space-y-3'>
              <h4 className='text-sm font-medium text-gray-90 border-b border-gray-20 pb-2'>
                Order Items ({order.Items.length} items)
              </h4>
              <div className='max-h-32 overflow-y-auto'>
                <div className='space-y-2'>
                  {order.Items.map((item, index) => (
                    <div
                      key={index}
                      className='flex justify-between items-center text-sm py-1 border-b border-gray-10 last:border-b-0'
                    >
                      <span className='text-gray-90'>{item.name}</span>
                      <div className='flex items-center space-x-4 text-gray-60'>
                        <span>Qty: {item.quantity}</span>
                        <span>
                          {formatCurrency(
                            item.subTotal || item.unitPrice * item.quantity
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Update Forms */}
        <form onSubmit={handleSubmit(onSubmit as any)} className='space-y-6'>
          <div className='bg-white border border-gray-20 rounded-lg p-6 space-y-6'>
            <h4 className='text-md font-medium text-gray-90 border-b border-gray-20 pb-3'>
              Update Order Status
            </h4>

            {/* Order Status Update */}
            <div className='space-y-2'>
              <Label
                htmlFor='status'
                className='text-sm font-medium text-gray-70'
              >
                Order Status <span className='text-destructive-50'>*</span>
              </Label>
              <Controller
                name='status'
                control={updateControl}
                rules={{ required: 'Order status is required' }}
                render={({ field, fieldState }) => (
                  <FilterSelect
                    name='status'
                    value={field.value}
                    options={statusOptions}
                    placeholder='Select order status'
                    onChange={field.onChange}
                    className='w-full'
                    error={fieldState?.error?.message}
                  />
                )}
              />
              <p className='text-xs text-gray-50'>
                Current status:{' '}
                <span className='font-medium capitalize'>
                  {capitalizeFirstLetter(order.status)}
                </span>
              </p>
            </div>

            {/* Payment Status Update */}
            <div className='space-y-2'>
              <Label
                htmlFor='paymentStatus'
                className='text-sm font-medium text-gray-70'
              >
                Payment Status
              </Label>
              <Controller
                name='paymentStatus'
                control={updateControl}
                render={({ field, fieldState }) => (
                  <FilterSelect
                    name='paymentStatus'
                    value={field.value}
                    options={paymentStatusOptions}
                    placeholder='Select payment status'
                    onChange={field.onChange}
                    className='w-full'
                    error={fieldState?.error?.message}
                  />
                )}
              />
              <p className='text-xs text-gray-50'>
                Current payment status:{' '}
                <span className='font-medium capitalize'>
                  {capitalizeFirstLetter(order.paymentStatus || 'Pending')}
                </span>
              </p>
            </div>

            {/* Notes */}
            <div className='space-y-2'>
              <Label
                htmlFor='notes'
                className='text-sm font-medium text-gray-70'
              >
                Update Notes (Optional)
              </Label>
              <Controller
                name='notes'
                control={updateControl}
                render={({ field, fieldState }) => (
                  <div className='space-y-2'>
                    <textarea
                      {...field}
                      placeholder='Add any notes about this status update...'
                      rows={4}
                      className='w-full p-3 border border-gray-30 rounded-lg resize-none focus:ring-2 focus:ring-success-50 focus:border-transparent outline-none'
                      maxLength={500}
                    />
                    <div className='flex justify-between items-center text-xs text-gray-50'>
                      <span>Optional: Add context for this update</span>
                      <span>{(field.value || '').length}/500</span>
                    </div>
                    {fieldState?.error?.message && (
                      <p className='text-sm text-destructive-60'>
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          {/* Status Change Preview */}
          {(watchedStatus !== order.status ||
            watchedPaymentStatus !== order.paymentStatus) && (
            <div className='bg-brand-5 border border-brand-20 rounded-lg p-4'>
              <h5 className='text-sm font-medium text-brand-90 mb-3'>
                Changes Preview
              </h5>
              <div className='space-y-2 text-sm'>
                {watchedStatus !== order.status && (
                  <div className='flex items-center justify-between'>
                    <span className='text-brand-70'>Order Status:</span>
                    <div className='flex items-center space-x-2'>
                      <span className='px-2 py-1 bg-gray-10 text-gray-80 rounded text-xs uppercase'>
                        {capitalizeFirstLetter(order.status)}
                      </span>
                      <span className='text-brand-50'>→</span>
                      <span className='px-2 py-1 bg-brand-10 text-brand-80 rounded text-xs uppercase'>
                        {capitalizeFirstLetter(watchedStatus)}
                      </span>
                    </div>
                  </div>
                )}
                {watchedPaymentStatus !== order.paymentStatus && (
                  <div className='flex items-center justify-between'>
                    <span className='text-brand-70'>Payment Status:</span>
                    <div className='flex items-center space-x-2'>
                      <span className='px-2 py-1 bg-gray-10 text-gray-80 rounded text-xs uppercase'>
                        {capitalizeFirstLetter(
                          order.paymentStatus || 'Pending'
                        )}
                      </span>
                      <span className='text-brand-50'>→</span>
                      <span className='px-2 py-1 bg-brand-10 text-brand-80 rounded text-xs uppercase'>
                        {capitalizeFirstLetter(watchedPaymentStatus)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    );
  };

  return (
    <div className='space-y-6'>
      {mode === 'create' && (
        <>
          <StepIndicator
            currentStep={currentStep}
            totalSteps={totalSteps}
            stepLabels={stepLabels}
          />
          <div className='min-h-[400px]'>{renderCreateStepContent()}</div>
        </>
      )}

      {mode === 'update' && renderUpdateForm()}
    </div>
  );
};

export default OrderForm;
